
import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Calendar, MapPin, Plus, Calendar as CalendarIcon, Clock, Coins } from "lucide-react";
import { format, isAfter } from "date-fns";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle, 
  DialogFooter,
  DialogTrigger 
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Calendar as CalendarPicker } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import { supabase } from "@/lib/supabase";
import { toast } from "sonner";

export interface Event {
  id: string | number;
  name: string;
  schedule: string;
  details?: string;
  location?: string;
  points?: number;
  totalActivity?: number;
  points_per_share?: number;
}

export interface EventsTabProps {
  userId: string;
  events: Event[];
  onCreateEvent?: () => void;
  totalPoints?: number;
}

const EventsTab: React.FC<EventsTabProps> = ({ userId, events, onCreateEvent, totalPoints = 0 }) => {
  const now = new Date();
  
  const [isCreateEventOpen, setIsCreateEventOpen] = useState(false);
  const [date, setDate] = useState<Date>();
  const [time, setTime] = useState<string>("12:00");
  const [eventName, setEventName] = useState<string>("");
  const [location, setLocation] = useState<string>("");
  const [details, setDetails] = useState<string>("");
  const [points, setPoints] = useState<number>(0);
  const [pointsPerShare, setPointsPerShare] = useState<number>(5);

  // Sort events into upcoming and past
  const upcomingEvents = events
    .filter(event => isAfter(new Date(event.schedule), now))
    .sort((a, b) => new Date(a.schedule).getTime() - new Date(b.schedule).getTime());
  
  const pastEvents = events
    .filter(event => !isAfter(new Date(event.schedule), now))
    .sort((a, b) => new Date(b.schedule).getTime() - new Date(a.schedule).getTime());

  const handleCreateEvent = async () => {
    if (!eventName) {
      toast.error("Please enter an event name");
      return;
    }

    if (!date) {
      toast.error("Please select a date");
      return;
    }

    if (points > totalPoints) {
      toast.error("You don't have enough points");
      return;
    }

    try {
      // Combine date and time
      const scheduleDate = new Date(date);
      const [hours, minutes] = time.split(":").map(Number);
      scheduleDate.setHours(hours, minutes);

      // Insert to Supabase
      const { data, error } = await supabase
        .from('events')
        .insert({
          event_name: eventName,
          schedule: scheduleDate.toISOString(),
          location: location,
          details: details,
          points: points,
          points_per_share: pointsPerShare,
          user_id: userId
        });

      if (error) {
        throw error;
      }

      toast.success("Event created successfully");
      setIsCreateEventOpen(false);
      
      // Reset form
      setEventName("");
      setLocation("");
      setDetails("");
      setPoints(0);
      setPointsPerShare(5);
      setDate(undefined);
      setTime("12:00");
      
      // Call onCreateEvent if provided
      if (onCreateEvent) {
        onCreateEvent();
      }
    } catch (error) {
      console.error("Error creating event:", error);
      toast.error("Failed to create event");
    }
  };
  
  const handleGetPoints = () => {
    // This will be implemented later
    toast.info("Get Points feature coming soon!");
  };
  
  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-bold">Events</h2>
        <Dialog open={isCreateEventOpen} onOpenChange={setIsCreateEventOpen}>
          <DialogTrigger asChild>
            <Button className="gap-2">
              <Plus className="h-4 w-4" />
              Start an Event
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-gray-900 text-white">
            <DialogHeader>
              <DialogTitle>Create New Event</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="event-name">Event Name</Label>
                <Input 
                  id="event-name" 
                  placeholder="Enter event name" 
                  value={eventName}
                  onChange={(e) => setEventName(e.target.value)}
                  className="text-black"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="location">Location</Label>
                <Input 
                  id="location" 
                  placeholder="Enter event location"
                  value={location}
                  onChange={(e) => setLocation(e.target.value)}
                  className="text-black"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Date</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className={cn(
                          "w-full justify-start text-left font-normal",
                          !date && "text-muted-foreground"
                        )}
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {date ? format(date, "PPP") : "Pick a date"}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0 bg-white" align="start">
                      <CalendarPicker
                        mode="single"
                        selected={date}
                        onSelect={setDate}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="time">Time</Label>
                  <div className="flex items-center">
                    <Input 
                      id="time" 
                      type="time"
                      value={time}
                      onChange={(e) => setTime(e.target.value)}
                      className="w-full text-black"
                    />
                  </div>
                </div>
              </div>
              
              <div className="space-y-2">
                <div className="flex justify-between items-center">
                  <Label htmlFor="points">Points</Label>
                  <span className="text-sm text-gray-400">Available: {totalPoints}</span>
                </div>
                <div className="flex gap-2">
                  <Input 
                    id="points" 
                    type="number"
                    min={0}
                    max={totalPoints}
                    value={points}
                    onChange={(e) => setPoints(Math.min(Number(e.target.value), totalPoints))}
                    className="text-black"
                  />
                  <Button onClick={handleGetPoints} className="whitespace-nowrap gap-1">
                    <Coins className="h-4 w-4" />
                    Get Points
                  </Button>
                </div>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="details">Details</Label>
                <Textarea 
                  id="details" 
                  placeholder="Enter event details"
                  value={details}
                  onChange={(e) => setDetails(e.target.value)}
                  className="h-20 text-black"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="points-per-share">Points Per Share</Label>
                <Input 
                  id="points-per-share" 
                  type="number"
                  min={0}
                  value={pointsPerShare}
                  onChange={(e) => setPointsPerShare(Number(e.target.value))}
                  className="text-black"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setIsCreateEventOpen(false)}>
                Cancel
              </Button>
              <Button onClick={handleCreateEvent}>
                Save Event
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {/* Upcoming Events */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between pb-2">
          <div>
            <CardTitle>Upcoming Events</CardTitle>
            <CardDescription>Events scheduled in the future</CardDescription>
          </div>
        </CardHeader>
        <CardContent>
          {upcomingEvents.length > 0 ? (
            <div className="space-y-4">
              {upcomingEvents.map((event, index) => (
                <div 
                  key={event.id}
                  className={`p-4 rounded-lg border ${
                    index === 0 ? 'bg-primary/5 border-primary/20' : 'border-gray-700'
                  }`}
                >
                  <div className="flex justify-between">
                    <h4 className="font-semibold">{event.name}</h4>
                    {event.points !== undefined && (
                      <span className="text-sm font-medium text-yellow-600">
                        {event.points} points
                      </span>
                    )}
                  </div>
                  
                  <div className="mt-2 flex items-center text-sm text-muted-foreground">
                    <Calendar className="h-3 w-3 mr-1" />
                    <span>{format(new Date(event.schedule), 'PPP p')}</span>
                  </div>
                  
                  {event.location && (
                    <div className="mt-1 flex items-center text-sm text-muted-foreground">
                      <MapPin className="h-3 w-3 mr-1" />
                      <span>{event.location}</span>
                    </div>
                  )}
                  
                  {event.details && (
                    <p className="mt-2 text-sm">{event.details}</p>
                  )}
                  
                  {event.totalActivity !== undefined && (
                    <div className="mt-2 text-xs text-muted-foreground">
                      <span>Activity goal: {event.totalActivity}</span>
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-6 bg-muted/20 rounded-lg">
              <p className="text-muted-foreground">No upcoming events</p>
            </div>
          )}
        </CardContent>
      </Card>
      
      {/* Past Events */}
      {pastEvents.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Past Events</CardTitle>
            <CardDescription>Previously scheduled events</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {pastEvents.map((event) => (
                <div 
                  key={event.id}
                  className="p-4 rounded-lg border border-gray-700 bg-gray-800/30"
                >
                  <div className="flex justify-between">
                    <h4 className="font-semibold">{event.name}</h4>
                    {event.points !== undefined && (
                      <span className="text-sm font-medium text-yellow-600">
                        {event.points} points
                      </span>
                    )}
                  </div>
                  
                  <div className="mt-2 flex items-center text-sm text-muted-foreground">
                    <Calendar className="h-3 w-3 mr-1" />
                    <span>{format(new Date(event.schedule), 'PPP p')}</span>
                  </div>
                  
                  {event.location && (
                    <div className="mt-1 flex items-center text-sm text-muted-foreground">
                      <MapPin className="h-3 w-3 mr-1" />
                      <span>{event.location}</span>
                    </div>
                  )}
                  
                  {event.details && (
                    <p className="mt-2 text-sm">{event.details}</p>
                  )}
                  
                  {event.totalActivity !== undefined && (
                    <div className="mt-2 text-xs text-muted-foreground">
                      <span>Activity goal: {event.totalActivity}</span>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default EventsTab;
